import type { AccountantTenantExoneratedRegime } from "@fiscalo/shared-types";

import { apiClient, apiBaseUrl } from "@/services/apiClient";

export type FiscalCompanyType = "unipersonal" | "sas" | "srl" | "sa";
/**
 * "MONOTRIBUTO" is the BPS unified regime — a single monthly aporte that
 * substitutes IVA + IRAE, so it never coexists with them on a profile.
 */
export type FiscalTaxType =
  | "IVA"
  | "IRAE"
  | "IRPF"
  | "IRNR"
  | "BPS"
  | "MONOTRIBUTO"
  /** Monthly SNIS servicios personales — profesionales que aportan a la Caja Profesional. */
  | "FONASA"
  /** Annual declaración de Fonasa de Servicios Personales, presented in April. */
  | "FONASA_ANUAL";
export type IvaFrequency = "monthly" | "minimum" | "annual" | "bimonthly";
export type IraeFrequency = "annual" | "monthly";
export type IvaRegime =
  | "minimum"
  | "cede"
  | "no_cede"
  | "servicios_personales";
export type IvaMinimumBenefit = "standard" | "efactura_3_3";

export interface FiscalProfile {
  id: string;
  companyId: string;
  companyType: FiscalCompanyType;
  isExporter: boolean;
  hasEmployees: boolean;
  taxes: FiscalTaxType[];
  ivaFrequency: IvaFrequency | null;
  ivaRegime?: IvaRegime | null;
  /** Régimen especial (Res. DGI 1222/008 automotoras); null clears it. */
  specialRegime?: string | null;
  ivaMinimumBenefit?: IvaMinimumBenefit | null;
  /**
   * Régimen-general client's self-declared opt-in to the 80 UI fiscal benefit
   * (Decreto 206/019). null = unspecified. Literal E gets the benefit
   * automatically, so this field is only meaningful for régimen general.
   */
  fiscalBenefitOptIn?: boolean | null;
  iraeFrequency: IraeFrequency | null;
  fiscalYearEnd: string;
  activityStartDate: string;
  bpsCompanyNumber?: string | null;
  taxpayerType?: "industria_comercio" | "rural" | "construccion" | null;
  paysIVA?: boolean | null;
  paysIRAE?: boolean | null;
  paysBPS?: boolean | null;
  /**
   * Profesional que aporta a la Caja Profesional: at BPS they pay FONASA
   * (servicios personales) and no aporte jubilatorio. Null = not asked.
   */
  paysFonasaServiciosPersonales?: boolean | null;
  /** When true, Fiscalo auto-generates the IVA boleto on the first business day of each month. Default true. */
  autoGenerateIvaBoleto?: boolean;
  /**
   * When true, Fiscalo emails the accountant on the 1st of each month asking
   * for the BPS boleto. Default FALSE — unset means off.
   */
  autoRequestBpsBoleto?: boolean;
  /**
   * Audit metadata from the deriver service (DGI registral data → profile).
   * Null when no derivation has run yet (manual signup, no OCR step).
   */
  derivedMetadata?: {
    needsReview: boolean;
    reviewReasons: string[];
    derivedAt: string;
    trace: {
      companyTypeSource?: string;
      ivaRegimeSource?: string;
      isExporterSource?: string;
      taxpayerTypeSource?: string;
      fiscalYearEndSource?: string;
      obligacionesMatched?: Array<{ nombre: string; effects: string[] }>;
    };
  } | null;
  company?: { rut?: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFiscalProfilePayload {
  companyType: FiscalCompanyType;
  isExporter: boolean;
  hasEmployees: boolean;
  taxes: FiscalTaxType[];
  ivaFrequency?: IvaFrequency;
  ivaRegime?: IvaRegime | null;
  /** Régimen especial (Res. DGI 1222/008 automotoras); null clears it. */
  specialRegime?: string | null;
  ivaMinimumBenefit?: IvaMinimumBenefit | null;
  fiscalBenefitOptIn?: boolean | null;
  iraeFrequency?: IraeFrequency;
  fiscalYearEnd: string;
  activityStartDate: string;
  bpsCompanyNumber?: string | null;
  taxpayerType?: "industria_comercio" | "rural" | "construccion" | null;
  paysIVA?: boolean | null;
  paysIRAE?: boolean | null;
  paysBPS?: boolean | null;
  paysFonasaServiciosPersonales?: boolean | null;
  autoRequestBpsBoleto?: boolean;
}

export interface UpdateFiscalProfilePayload {
  companyType?: FiscalCompanyType;
  isExporter?: boolean;
  hasEmployees?: boolean;
  taxes?: FiscalTaxType[];
  ivaFrequency?: IvaFrequency | null;
  ivaRegime?: IvaRegime | null;
  /** Régimen especial (Res. DGI 1222/008 automotoras); null clears it. */
  specialRegime?: string | null;
  ivaMinimumBenefit?: IvaMinimumBenefit | null;
  fiscalBenefitOptIn?: boolean | null;
  iraeFrequency?: IraeFrequency | null;
  fiscalYearEnd?: string;
  activityStartDate?: string;
  bpsCompanyNumber?: string | null;
  taxpayerType?: "industria_comercio" | "rural" | "construccion" | null;
  paysIVA?: boolean | null;
  paysIRAE?: boolean | null;
  paysBPS?: boolean | null;
  paysFonasaServiciosPersonales?: boolean | null;
  autoGenerateIvaBoleto?: boolean;
  autoRequestBpsBoleto?: boolean;
}

export type AutoBoletoState =
  | "success_auto"
  | "success_manual"
  | "in_progress"
  | "failed"
  | "scheduled"
  | "disabled"
  | "hidden";

export interface AutoBoletoStatus {
  state: AutoBoletoState;
  lastAttemptAt: string | null;
  nextScheduledDate: string | null;
  message: string | null;
}

export type AutomationEventKind =
  | "boleto-auto-generated"
  | "boleto-auto-failed"
  | "boleto-auto-in-progress"
  | "bps-doc-auto-requested";

export interface AutomationEvent {
  kind: AutomationEventKind;
  occurredAt: string;
  obligationId?: string;
  period?: string;
  taxLabel?: string;
  message?: string;
}

/** Batched per-IVA-obligation chip status. Returns 'hidden' for ids the chip should not render against. */
export async function getAutoBoletoStatuses(
  obligationIds: string[],
): Promise<Record<string, AutoBoletoStatus>> {
  if (obligationIds.length === 0) return {};
  const { data } = await apiClient.get<Record<string, AutoBoletoStatus>>(
    "/fiscal-automations/auto-boleto-status",
    { params: { obligationIds: obligationIds.join(",") } },
  );
  return data ?? {};
}

/** Recent automated actions for the dashboard widget. Default 14-day window. */
export async function getRecentAutomations(days = 14): Promise<AutomationEvent[]> {
  const { data } = await apiClient.get<AutomationEvent[]>(
    "/fiscal-automations/recent",
    { params: { days } },
  );
  return data ?? [];
}

/**
 * Fetches the current tenant's fiscal profile. Returns null if none exists (200 with null body).
 */
export async function getFiscalProfile(): Promise<FiscalProfile | null> {
  const { data } = await apiClient.get<FiscalProfile | null>("/fiscal-profile");
  return data ?? null;
}

export async function createFiscalProfile(
  payload: CreateFiscalProfilePayload
): Promise<FiscalProfile> {
  const { data } = await apiClient.post<FiscalProfile>("/fiscal-profile", payload);
  return data;
}

export async function updateFiscalProfile(
  payload: UpdateFiscalProfilePayload
): Promise<FiscalProfile> {
  const { data } = await apiClient.put<FiscalProfile>("/fiscal-profile", payload);
  return data;
}

/**
 * Accountant-scoped (membership-gated) read of a client's fiscal profile —
 * for the accountant tenant-detail "Perfil fiscal" tab. Null if none exists.
 */
export async function getFiscalProfileForTenant(
  tenantId: string
): Promise<FiscalProfile | null> {
  const { data } = await apiClient.get<FiscalProfile | null>(
    `/dashboard/accountant/tenants/${tenantId}/fiscal-profile`
  );
  return data ?? null;
}

export async function updateFiscalProfileForTenant(
  tenantId: string,
  payload: UpdateFiscalProfilePayload
): Promise<FiscalProfile> {
  const { data } = await apiClient.put<FiscalProfile>(
    `/dashboard/accountant/tenants/${tenantId}/fiscal-profile`,
    payload
  );
  return data;
}

/**
 * Accountant-scoped read of a client's exonerated-regime branding. Separate
 * from the fiscal-profile calls because these fields live on the company row,
 * not on fiscal_profile.
 */
export async function getExoneratedRegimeForTenant(
  tenantId: string
): Promise<AccountantTenantExoneratedRegime> {
  const { data } = await apiClient.get<AccountantTenantExoneratedRegime>(
    `/dashboard/accountant/tenants/${tenantId}/exonerated-regime`
  );
  return data;
}

export async function updateExoneratedRegimeForTenant(
  tenantId: string,
  payload: {
    exoneratedRegimeEnabled?: boolean | null;
    exoneratedRegimeLegend?: string | null;
    exoneratedRegimeHeaderLegend?: string | null;
  }
): Promise<AccountantTenantExoneratedRegime> {
  const { data } = await apiClient.patch<AccountantTenantExoneratedRegime>(
    `/dashboard/accountant/tenants/${tenantId}/exonerated-regime`,
    payload
  );
  return data;
}

export type FiscalObligationAuthority = "DGI" | "BPS";

export interface FiscalAgendaObligation {
  id: string;
  companyId: string;
  taxType: FiscalTaxType;
  period: string;
  dueDate: string;
  /** First day of the annual filing window. Present only on annual obligations (IRAE/IRPF/IRNR). */
  filingStartDate?: string | null;
  status: "upcoming" | "due_today" | "dueSoon" | "overdue" | "completed";
  authority: FiscalObligationAuthority;
  source: string;
  acknowledgedAt: string | null;
  completedAt: string | null;
  notes: string | null;
  boletoRequestedAt: string | null;
  requestedByUserId: string | null;
  boletoInProgressAt: string | null;
  boletoNumber: string | null;
  boletoFileUrl: string | null;
  boletoUploadedAt: string | null;
  /** Actual amount the user paid for this period (UYU). Numeric column round-tripped as string by pg-node. */
  amountPaidUyu?: string | null;
  /**
   * System-computed amount due (UYU). For IVA this is refreshed daily from the
   * period's débito/crédito position. `"0.00"` with a positive saldoAFavorUyu is
   * "a declarar, sin importe a pagar" — see declareOnly.ts. Null = not computed.
   */
  amountOwedUyu?: string | null;
  /** IVA credit left after the period is settled (UYU), i.e. the carry-out. Null = not computed. */
  saldoAFavorUyu?: string | null;
  /**
   * BPS only, computed server-side: the later date an internet payer may
   * defer the PAGO to. NOT this obligation's deadline — the nómina /
   * declaración still falls on dueDate, which is what makes it vencida.
   */
  internetPaymentDueDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type TenantAccountantRequestStatus =
  | "pending"
  | "in_progress"
  | "completed";

export interface TenantAccountantRequest {
  id: string;
  tenantId: string;
  fiscalObligationId: string;
  requestedByUserId: string | null;
  requestType: string;
  status: TenantAccountantRequestStatus;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  fiscalObligation: FiscalAgendaObligation;
}

/** GET /fiscal-agenda/tenant-accountant-requests — list for current tenant. Pass statuses to filter (default: pending + in_progress). */
export async function getTenantAccountantRequests(
  statuses?: Array<"pending" | "in_progress" | "completed">
): Promise<TenantAccountantRequest[]> {
  const params =
    statuses && statuses.length > 0
      ? { statuses: statuses.join(",") }
      : undefined;
  const { data } = await apiClient.get<TenantAccountantRequest[]>(
    "/fiscal-agenda/tenant-accountant-requests",
    { params }
  );
  return data ?? [];
}

/** GET /fiscal-agenda/tenant-accountant-requests/:id — one request with obligation. */
export async function getTenantAccountantRequest(
  requestId: string
): Promise<TenantAccountantRequest | null> {
  const { data } = await apiClient.get<TenantAccountantRequest | null>(
    `/fiscal-agenda/tenant-accountant-requests/${requestId}`
  );
  return data ?? null;
}

/** GET /fiscal-agenda?from=YYYY-MM&to=YYYY-MM returns grouped by month (key = YYYY-MM). */
export async function getFiscalAgenda(
  from: string,
  to: string
): Promise<Record<string, FiscalAgendaObligation[]>> {
  const { data } = await apiClient.get<Record<string, FiscalAgendaObligation[]>>(
    "/fiscal-agenda",
    { params: { from, to } }
  );
  return data;
}

/** PATCH /fiscal-agenda/:id with status completed and optional completedDate, notes, amountPaidUyu. */
export async function markFiscalObligationCompleted(
  obligationId: string,
  payload?: {
    completedDate?: string;
    notes?: string;
    amountPaidUyu?: number | null;
  }
): Promise<FiscalAgendaObligation> {
  const { data } = await apiClient.patch<FiscalAgendaObligation>(
    `/fiscal-agenda/${obligationId}`,
    { status: "completed", ...payload }
  );
  return data;
}

/** PATCH /fiscal-agenda/:id with just amountPaidUyu — retroactively records what was paid for an
 * already-completed (or any) obligation. Does not touch status. Pass null to clear. */
export async function recordFiscalObligationPayment(
  obligationId: string,
  amountPaidUyu: number | null
): Promise<FiscalAgendaObligation> {
  const { data } = await apiClient.patch<FiscalAgendaObligation>(
    `/fiscal-agenda/${obligationId}`,
    { amountPaidUyu }
  );
  return data;
}

/** PATCH /fiscal-agenda/:id with status pending – revert completed obligation to pending. */
export async function markFiscalObligationUncompleted(
  obligationId: string
): Promise<FiscalAgendaObligation> {
  const { data } = await apiClient.patch<FiscalAgendaObligation>(
    `/fiscal-agenda/${obligationId}`,
    { status: "pending" }
  );
  return data;
}

/** POST /fiscal-agenda/:id/request-boleto – request payment slip (owner/admin/member). Emails accountants. */
export async function requestBoleto(
  obligationId: string
): Promise<FiscalAgendaObligation> {
  const { data } = await apiClient.post<FiscalAgendaObligation>(
    `/fiscal-agenda/${obligationId}/request-boleto`
  );
  return data;
}

/** PATCH /fiscal-agenda/:id/boleto-in-progress – mark boleto request in progress (accountant only). Notifies requester. */
export async function markBoletoInProgress(
  obligationId: string
): Promise<FiscalAgendaObligation> {
  const { data } = await apiClient.patch<FiscalAgendaObligation>(
    `/fiscal-agenda/${obligationId}/boleto-in-progress`
  );
  return data;
}

/** PATCH /fiscal-agenda/:id/boleto – upload payment slip (accountant only). */
export async function uploadBoleto(
  obligationId: string,
  payload: { boletoNumber: string; boletoFileUrl: string }
): Promise<FiscalAgendaObligation> {
  const { data } = await apiClient.patch<FiscalAgendaObligation>(
    `/fiscal-agenda/${obligationId}/boleto`,
    payload
  );
  return data;
}

/** DELETE /fiscal-agenda/:id/boleto – remove uploaded payment slip (accountant only). */
export async function removeBoleto(
  obligationId: string
): Promise<FiscalAgendaObligation> {
  const { data } = await apiClient.delete<FiscalAgendaObligation>(
    `/fiscal-agenda/${obligationId}/boleto`
  );
  return data;
}

/** POST /fiscal-agenda/:id/start-dgi-boleto – start DGI boleto automation. Returns sessionId for polling. */
export async function startDgiBoleto(
  obligationId: string,
  payload: { amount: number }
): Promise<{ sessionId: string }> {
  const { data } = await apiClient.post<{ sessionId: string }>(
    `/fiscal-agenda/${obligationId}/start-dgi-boleto`,
    payload
  );
  return data;
}

/** WebSocket URL for DGI boleto session updates (same host as API, path /ws/dgi-boleto-session). */
export function getDgiBoletoSessionWsUrl(): string {
  const base = apiBaseUrl.replace(/\/$/, "");
  const wsBase = base.startsWith("https")
    ? "wss" + base.slice(5)
    : base.startsWith("http")
      ? "ws" + base.slice(4)
      : base;
  return `${wsBase}/ws/dgi-boleto-session`;
}

/** GET /fiscal-agenda/dgi-boleto-session/:sessionId – get CAPTCHA image, status, current sub-step and failure reason. */
export async function getDgiBoletoSession(sessionId: string): Promise<{
  captchaImageBase64?: string;
  status?: string;
  currentStep?: string;
  lastError?: string | null;
}> {
  const { data } = await apiClient.get<{
    captchaImageBase64?: string;
    status?: string;
    currentStep?: string;
    lastError?: string | null;
  }>(`/fiscal-agenda/dgi-boleto-session/${sessionId}`);
  return data ?? {};
}

/** POST /fiscal-agenda/dgi-boleto-captcha – submit user CAPTCHA value for DGI boleto session. */
export async function submitDgiBoletoCaptcha(
  payload: { sessionId: string; captchaValue: string }
): Promise<void> {
  await apiClient.post("/fiscal-agenda/dgi-boleto-captcha", payload);
}

/** POST /fiscal-agenda/dgi-boleto-refresh-captcha – request new CAPTCHA image (automation reloads DGI page). */
export async function refreshDgiBoletoCaptcha(sessionId: string): Promise<void> {
  await apiClient.post("/fiscal-agenda/dgi-boleto-refresh-captcha", { sessionId });
}

/** POST /fiscal/dgi-consulta/start – start Identidades Digitales consulta automation. */
export async function startDgiConsulta(
  documentKey: string,
  options?: { signal?: AbortSignal }
): Promise<{ sessionId: string }> {
  const { data } = await apiClient.post<{ sessionId: string }>(
    "/fiscal/dgi-consulta/start",
    { documentKey },
    { signal: options?.signal }
  );
  return data;
}

/** WebSocket URL for DGI consulta session updates (path /ws/dgi-consulta-session). */
export function getDgiConsultaSessionWsUrl(): string {
  const base = apiBaseUrl.replace(/\/$/, "");
  const wsBase = base.startsWith("https")
    ? "wss" + base.slice(5)
    : base.startsWith("http")
      ? "ws" + base.slice(4)
      : base;
  return `${wsBase}/ws/dgi-consulta-session`;
}

/** GET /fiscal/dgi-consulta/session/:sessionId */
export async function getDgiConsultaSession(sessionId: string): Promise<{
  status?: string;
  currentStep?: string;
  documentKey?: string;
  resultPdfUrl?: string;
  lastError?: string;
}> {
  const { data } = await apiClient.get<{
    status?: string;
    currentStep?: string;
    documentKey?: string;
    resultPdfUrl?: string;
    lastError?: string;
  }>(`/fiscal/dgi-consulta/session/${sessionId}`);
  return data ?? {};
}

export type DgiConsultaIdpChoice = "abitab" | "tuid";

/** POST /fiscal/dgi-consulta/step – merge user input for Playwright poll. */
export async function submitDgiConsultaStep(payload: {
  sessionId: string;
  idpChoice?: DgiConsultaIdpChoice;
  abitabDocumentType?: "cedula" | "pasaporte";
  abitabDocumentNumber?: string;
  tuidAppMobileConfirmed?: boolean;
  tuidDocument?: string;
}): Promise<void> {
  await apiClient.post("/fiscal/dgi-consulta/step", payload);
}

/** POST /fiscal/dgi-consulta/cancel – stop automation (user closed modal or cancelled). */
export async function cancelDgiConsulta(sessionId: string): Promise<void> {
  await apiClient.post("/fiscal/dgi-consulta/cancel", { sessionId });
}

/** POST /fiscal/dgi-portal-user-automation/start – e-Factura portal user form automation (onboarding). */
export async function startDgiPortalUserAutomation(options?: {
  signal?: AbortSignal;
}): Promise<{ sessionId: string }> {
  const { data } = await apiClient.post<{ sessionId: string }>(
    "/fiscal/dgi-portal-user-automation/start",
    {},
    { signal: options?.signal }
  );
  return data;
}

/** GET /fiscal/dgi-portal-user-automation/session/:sessionId */
export async function getDgiPortalUserAutomationSession(sessionId: string): Promise<{
  status?: string;
  currentStep?: string;
  documentKey?: string;
  resultPdfUrl?: string;
  lastError?: string;
}> {
  const { data } = await apiClient.get<{
    status?: string;
    currentStep?: string;
    documentKey?: string;
    resultPdfUrl?: string;
    lastError?: string;
  }>(`/fiscal/dgi-portal-user-automation/session/${sessionId}`);
  return data ?? {};
}

/** POST /fiscal/dgi-portal-user-automation/step – same shape as DGI consulta step. */
export async function submitDgiPortalUserAutomationStep(payload: {
  sessionId: string;
  idpChoice?: DgiConsultaIdpChoice;
  abitabDocumentType?: "cedula" | "pasaporte";
  abitabDocumentNumber?: string;
  tuidAppMobileConfirmed?: boolean;
  tuidDocument?: string;
  confirmDeleteExistingUser?: boolean;
}): Promise<void> {
  await apiClient.post("/fiscal/dgi-portal-user-automation/step", payload);
}

/** POST /fiscal/dgi-portal-user-automation/cancel */
export async function cancelDgiPortalUserAutomation(sessionId: string): Promise<void> {
  await apiClient.post("/fiscal/dgi-portal-user-automation/cancel", { sessionId });
}

export interface FiscalHealthFactors {
  overdueCount: number;
  dueIn3Count: number;
  missingBpsNumber: boolean;
  currentMonthComplete: boolean;
  /** Received CFEs whose 20-day accept/reject deadline has passed. */
  pendingAckOverdueCount: number;
  /** Received CFEs aged 17–19 days, inside the final-warning window. */
  pendingAckDueSoonCount: number;
  /** Received CFEs aged 0–16 days, still well within the window. */
  pendingAckFreshCount: number;
}

/** GET /fiscal-health returns obligation-based score and factors. */
export async function getFiscalHealth(): Promise<{
  score: number;
  factors: FiscalHealthFactors;
}> {
  const { data } = await apiClient.get<{
    score: number;
    factors: FiscalHealthFactors;
  }>("/fiscal-health");
  return data;
}

// --- DGI Onboarding Auto Flow ---

export async function startOnboardingAutoFlow(): Promise<{
  sessionId: string;
  accepted: boolean;
  message?: string;
}> {
  const { data } = await apiClient.post<{
    sessionId: string;
    accepted: boolean;
    message?: string;
  }>("/fiscal/dgi-onboarding-auto/start");
  return data;
}

/**
 * Accountant-only: request that Fiscalo be registered as a software
 * provider on the client's DGI portal. Skips the rest of the auto-flow
 * (portal_user, company_info, CAEs, last numbers) — accountants handle
 * those themselves. Triggers the manual-intervention path on the
 * backend; the team takes it from there.
 */
export async function requestProveedorSoftwareRegistration(): Promise<{
  accepted: true;
}> {
  const { data } = await apiClient.post<{ accepted: true }>(
    "/fiscal/dgi-onboarding-auto/request-proveedor-software-registration",
  );
  return data;
}

/**
 * Trigger a scrape of the tenant's last-used IdEmisor from DGI's Envíos
 * Realizados page and bump company_dgi_credentials.idEmisor accordingly.
 * Idempotent — backend skips if already synced. Returns immediately; the
 * actual scrape runs async and writes the value via webhook callback.
 */
export async function requestIdemisorSync(): Promise<{
  accepted: boolean;
  sessionId?: string;
  message?: string;
}> {
  const { data } = await apiClient.post<{
    accepted: boolean;
    sessionId?: string;
    message?: string;
  }>("/fiscal/dgi-onboarding-auto/request-idemisor-sync");
  return data;
}

/**
 * Accountant merged "Configuración Fiscalo en DGI" button: drives
 * whichever of idemisor_sync / proveedor_software is still pending.
 * Happy path: idemisor_sync fires and its success webhook auto-chains
 * proveedor_software. If a previous chain was interrupted and only
 * idemisor_sync stamped, the backend advances directly to
 * proveedor_software — the button is never a dead end.
 */
export async function startFiscaloConfig(): Promise<{
  accepted: boolean;
  idemisorSyncSessionId?: string;
  proveedorSoftwareSessionId?: string;
  message?: string;
}> {
  const { data } = await apiClient.post<{
    accepted: boolean;
    idemisorSyncSessionId?: string;
    proveedorSoftwareSessionId?: string;
    message?: string;
  }>("/fiscal/dgi-onboarding-auto/start-fiscalo-config");
  return data;
}

/**
 * Server-side check: is the merged "Configuración Fiscalo en DGI" chain
 * currently running for this tenant? Used to re-hydrate the loading
 * spinner after a page refresh — local state was lost, but the Python
 * automation can still be mid-flight.
 */
export async function getFiscaloConfigRunning(): Promise<{
  running: boolean;
  step: "idemisor_sync" | "proveedor_software" | null;
}> {
  const { data } = await apiClient.get<{
    running: boolean;
    step: "idemisor_sync" | "proveedor_software" | null;
  }>("/fiscal/dgi-onboarding-auto/fiscalo-config-running");
  return data;
}

/**
 * User-driven manual retry from the dashboard onboarding card.
 * Backend clears any `_failed` markers + retry counters and restarts
 * the chain (already-done steps are skipped via stamped timestamps).
 */
export async function retryDgiOnboardingAutoFlow(): Promise<{
  sessionId: string;
  accepted: boolean;
  message?: string;
}> {
  const { data } = await apiClient.post<{
    sessionId: string;
    accepted: boolean;
    message?: string;
  }>("/fiscal/dgi-onboarding-auto/retry");
  return data;
}

/**
 * Undo the "Fiscalo se está encargando" escalation a manual-DGI tenant
 * triggered by clicking Configurar on the proveedor_software row. Clears
 * the `_failed.proveedor_software = 'manual_intervention'` marker so the
 * dashboard row reverts to pending and the user can act on it again.
 */
export async function cancelManualProveedorRequest(): Promise<{ ok: true }> {
  const { data } = await apiClient.post<{ ok: true }>(
    "/fiscal/dgi-onboarding-auto/cancel-manual-proveedor-request",
  );
  return data;
}

export async function continueOnboardingCompanyInfo(
  orchestrationSessionId: string,
): Promise<{ accepted: boolean; companyInfoSessionId?: string }> {
  const { data } = await apiClient.post<{
    accepted: boolean;
    companyInfoSessionId?: string;
  }>("/fiscal/dgi-onboarding-auto/continue-company-info", {
    orchestrationSessionId,
  });
  return data;
}

export async function continueOnboardingLastNumbers(
  orchestrationSessionId: string,
  caeTypes?: string[],
): Promise<{ accepted: boolean; lastNumbersSessionId?: string }> {
  const { data } = await apiClient.post<{
    accepted: boolean;
    lastNumbersSessionId?: string;
  }>("/fiscal/dgi-onboarding-auto/continue-last-numbers", {
    orchestrationSessionId,
    caeTypes,
  });
  return data;
}

export async function continueOnboardingCaeDownload(
  orchestrationSessionId: string,
  storageStateBase64?: string,
  lastUsedNumbers?: Record<string, number>,
): Promise<{ accepted: boolean; caeSessionId?: string }> {
  const { data } = await apiClient.post<{
    accepted: boolean;
    caeSessionId?: string;
  }>("/fiscal/dgi-onboarding-auto/continue-cae-download", {
    orchestrationSessionId,
    storageStateBase64,
    lastUsedNumbers,
  });
  return data;
}

export async function startOnboardingSsoRefresh(): Promise<{
  accepted: boolean;
  sessionId: string;
  message?: string;
}> {
  const { data } = await apiClient.post<{
    accepted: boolean;
    sessionId: string;
    message?: string;
  }>("/fiscal/dgi-onboarding-auto/start-sso-refresh");
  return data;
}

export async function continueOnboardingIdemisorSync(
  orchestrationSessionId: string,
): Promise<{
  accepted: boolean;
  idemisorSyncSessionId?: string;
  proveedorSoftwareSessionId?: string;
  idemisorSyncSkipped?: boolean;
}> {
  const { data } = await apiClient.post<{
    accepted: boolean;
    idemisorSyncSessionId?: string;
    proveedorSoftwareSessionId?: string;
    idemisorSyncSkipped?: boolean;
  }>("/fiscal/dgi-onboarding-auto/continue-idemisor-sync", {
    orchestrationSessionId,
  });
  return data;
}

export async function continueOnboardingProveedorSoftware(
  orchestrationSessionId: string,
): Promise<{ accepted: boolean; proveedorSoftwareSessionId?: string }> {
  const { data } = await apiClient.post<{
    accepted: boolean;
    proveedorSoftwareSessionId?: string;
  }>("/fiscal/dgi-onboarding-auto/continue-proveedor-software", {
    orchestrationSessionId,
  });
  return data;
}

export async function getOnboardingAutoSession(
  sessionId: string,
): Promise<Record<string, unknown>> {
  const { data } = await apiClient.get<Record<string, unknown>>(
    `/fiscal/dgi-onboarding-auto/session/${sessionId}`,
  );
  return data;
}

export async function reportOnboardingFailure(
  sessionId: string,
  step: string,
  error: string,
): Promise<void> {
  await apiClient.post("/fiscal/dgi-onboarding-auto/report-failure", {
    sessionId,
    step,
    error,
  });
}

/**
 * Why a client says they qualify for the 80 UI credit (Decreto 206/019). These
 * are DGI's three conditions and they are not interchangeable — the motivo
 * selects which flow applies:
 *  - `iva_minimo` (Art.5): Fiscalo discounts the credit off the price and
 *    recovers it on its own return.
 *  - `inicio_actividades` / `ingresos_bajo_750k` (Art.4): the client pays in
 *    full and offsets the credit on their own return.
 */
export type FiscalBenefitMotivo =
  | "iva_minimo"
  | "inicio_actividades"
  | "ingresos_bajo_750k";

export interface FiscalBenefitDeclaration {
  id: string;
  companyId: string;
  complies: boolean;
  motivo: FiscalBenefitMotivo | null;
  declaredByUserId: string | null;
  decreto: string;
  declaredAt: string;
}

export interface RecordFiscalBenefitDeclarationPayload {
  complies: boolean;
  motivo?: FiscalBenefitMotivo | null;
}

/** The signed-in tenant's own declaration, or null if never asked. */
export async function getFiscalBenefitDeclaration(): Promise<FiscalBenefitDeclaration | null> {
  const { data } = await apiClient.get<FiscalBenefitDeclaration | null>(
    "/tenants/current/fiscal-benefit-declaration",
  );
  return data;
}

export async function recordFiscalBenefitDeclaration(
  payload: RecordFiscalBenefitDeclarationPayload,
): Promise<FiscalBenefitDeclaration> {
  const { data } = await apiClient.post<FiscalBenefitDeclaration>(
    "/tenants/current/fiscal-benefit-declaration",
    payload,
  );
  return data;
}

/** Accountant-scoped read for a cartera client. */
export async function getFiscalBenefitDeclarationForTenant(
  tenantId: string,
): Promise<FiscalBenefitDeclaration | null> {
  const { data } = await apiClient.get<FiscalBenefitDeclaration | null>(
    `/dashboard/accountant/tenants/${tenantId}/fiscal-benefit-declaration`,
  );
  return data;
}

/**
 * Accountant records the declaration on the client's behalf. Role-gated
 * server-side (owner/admin/accountant) — a read_only collaborator cannot sign
 * an attestation Fiscalo relies on for its own Form 2181 filing.
 */
export async function recordFiscalBenefitDeclarationForTenant(
  tenantId: string,
  payload: RecordFiscalBenefitDeclarationPayload,
): Promise<FiscalBenefitDeclaration> {
  const { data } = await apiClient.post<FiscalBenefitDeclaration>(
    `/dashboard/accountant/tenants/${tenantId}/fiscal-benefit-declaration`,
    payload,
  );
  return data;
}
